import React, { useEffect, useState } from "react";
import Drawer from "../Components/Drawer";
import {
  UNEXPECTED_RESPONSE_MESSAGE,
  parseJsonResponseBody,
} from "../utils/apiErrors";
import { csrfToken } from "../utils/csrf";
import TicketDetailContent from "./TicketDetailContent";

export default function TicketDetailDrawer({ ticketId, onClose }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticketId) {
      setTicket(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setTicket(null);

    fetch(`/tickets/${ticketId}.json`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken() ?? "",
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        const parsed = parseJsonResponseBody(await response.text());
        if (!response.ok || !parsed.ok) {
          throw new Error(UNEXPECTED_RESPONSE_MESSAGE);
        }
        return parsed.data;
      })
      .then((data) => {
        setTicket(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message || UNEXPECTED_RESPONSE_MESSAGE);
        setLoading(false);
      });

    return () => controller.abort();
  }, [ticketId]);

  const handleMessageCreated = (newMessage) => {
    setTicket((prev) =>
      prev
        ? { ...prev, messages: [...(prev.messages ?? []), newMessage] }
        : prev,
    );
  };

  if (!ticketId) {
    return null;
  }

  return (
    <Drawer
      open
      onClose={onClose}
      ariaLabelledBy="ticket-detail-drawer-title"
      backdropLabel="Close ticket panel"
      panelClassName="max-w-xl"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-start justify-end border-b border-[var(--color-border)] px-4 py-3">
          <button
            type="button"
            className="rounded border border-[var(--color-border)] px-2 py-1 text-sm"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <span id="ticket-detail-drawer-title" className="sr-only">
          Ticket details
        </span>

        {loading ? (
          <p className="px-6 py-5 text-sm text-muted">Loading ticket…</p>
        ) : null}

        {error ? (
          <p className="px-6 py-5 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        ) : null}

        {ticket && !loading ? (
          <TicketDetailContent
            {...ticket}
            onMessageCreated={handleMessageCreated}
          />
        ) : null}
      </div>
    </Drawer>
  );
}
